import Link from 'next/link';
import styled from 'styled-components';
// #TODO: @goatapp/web-content-components is 451.8kb (gzippped: 131.6kb)
import { Space } from '@goatapp/web-content-components';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';

import { ITabName } from 'checkout/types/Tab';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { transformSpaces } from 'gift-cards/utils/transformSpaces';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { variableFontRegular } from 'styles/theme/text';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { guestPurchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Summary';

interface IGiftCardSummaryProps {
  className?: string;
  showPriceBreakdown?: boolean;
  showSupportLinks?: boolean;
  showTitle?: boolean;
}

export const GiftCardSummary = ({
  showPriceBreakdown = true,
  showSupportLinks = true,
}: IGiftCardSummaryProps) => {
  const router = useRouter();

  const { giftCard, order, tab, isGuestCheckout } = useCheckoutContext();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );

  let space = null;

  if (!isEmpty(giftCard.space)) {
    space = transformSpaces(giftCard.space);
  }

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });

  const handleTopicLinkClick = (topic: string) => {
    sendTrackingEvent('CHECKOUT_FAQ_TAP', {
      faq_topic: topic,
      product_template_slug: order?.product?.productTemplate?.slug,
    });
  };
  return (
    <GiftCardSummary.Container>
      <GiftCardSummary.Title data-qa="checkout_gift_card_summary_header">
        {t(`${TP}.orderReviewOrderTitle`, 'Your Order')}
      </GiftCardSummary.Title>
      <GiftCardSummary.Table>
        <GiftCardSummary.Image>
          {!isEmpty(giftCard.space) && <Space maxWidth={80} space={space} />}
        </GiftCardSummary.Image>
        <GiftCardSummary.Description>
          <GiftCardSummary.HeaderText>
            {t(`${TP}.giftCardSummaryDescription`, 'Gift Card')}
          </GiftCardSummary.HeaderText>
        </GiftCardSummary.Description>
        <GiftCardSummary.Recipient>
          <GiftCardSummary.Text>
            {t(`${TP}.giftCardSummaryRecipient`, 'Recipient')}
          </GiftCardSummary.Text>
        </GiftCardSummary.Recipient>
        <GiftCardSummary.RecipientValue data-qa="checkout_gift_card_summary_recipient">
          <GiftCardSummary.Text>{giftCard?.recipientName}</GiftCardSummary.Text>
          <GiftCardSummary.Text>({giftCard?.recipientEmail})</GiftCardSummary.Text>
        </GiftCardSummary.RecipientValue>
        {giftCard?.message && (
          <>
            <GiftCardSummary.Message>
              <GiftCardSummary.Text>
                {t(`${TP}.giftCardSummaryMessage`, 'Message')}
              </GiftCardSummary.Text>
            </GiftCardSummary.Message>
            <GiftCardSummary.MessageValue data-qa="checkout_gift_card_summary_message">
              <GiftCardSummary.Text>&ldquo;{giftCard?.message}&rdquo;</GiftCardSummary.Text>
            </GiftCardSummary.MessageValue>
          </>
        )}
      </GiftCardSummary.Table>
      {tab !== ITabName.REVIEW && showPriceBreakdown && (
        <GiftCardSummary.PriceBreakdown>
          <GiftCardSummary.TotalText>
            {t(`${TP}.giftCardSummaryOrderTotal`, 'Total')}
          </GiftCardSummary.TotalText>
          <GiftCardSummary.TotalText>
            {/* As of now giftcards are always in USD */}
            {formatMoneyObject({
              currency: 'USD',
              amount: isGuestPurchasingGiftCard
                ? Number(router?.query?.priceCents)
                : order?.localizedFinalPriceCents?.amountUsdCents,
            })}
          </GiftCardSummary.TotalText>
        </GiftCardSummary.PriceBreakdown>
      )}
      {showSupportLinks && (
        <>
          <GiftCardSummary.HorizontalRule />
          <Link href="/gift-card-terms" passHref legacyBehavior>
            <GiftCardSummary.Links
              target="_blank"
              rel="noreferrer"
              onClick={() => handleTopicLinkClick('Gift Card Terms')}
              data-qa="checkout_gift_card_summary_gift_card_terms_link"
            >
              {t(`${TP}.giftCardTerms`, 'Gift Card Terms')}
            </GiftCardSummary.Links>
          </Link>

          <GiftCardSummary.Links
            href="https://support.goat.com/hc/en-us/requests/new"
            target="_blank"
            rel="noreferrer"
            data-qa="checkout_gift_card_summary_support_link"
            onClick={() => handleTopicLinkClick('Contact Support')}
          >
            {t(`${TP}.contactSupportLink`, 'Contact Support')}
          </GiftCardSummary.Links>
        </>
      )}
    </GiftCardSummary.Container>
  );
};

GiftCardSummary.Container = styled.div`
  max-width: 66.66%;
  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding: 16px;
    max-width: unset;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin-left: 16px;
  }
`;

GiftCardSummary.Title = styled.div`
  padding: 8px 0;
  ${variableFontRegular({ size: '11.5px' })};
`;

GiftCardSummary.Table = styled.div`
  display: grid;
  margin-top: 16px;
  grid-template-columns:
    [image-start] 90px [image-end] 24px [content-start] auto [description] fit-content(50%)
    [price];
  grid-template-rows: [item-start] auto [recipient] auto [message] auto [item-end] 22px [total];
`;

GiftCardSummary.Image = styled.div`
  grid-column-start: image-start;
  grid-column-end: image-end;
  grid-row-start: item-start;
  grid-row-end: item-end;
`;

GiftCardSummary.Description = styled.div`
  grid-column-start: content-start;
  grid-column-end: price;
  grid-row-start: item-start;
  grid-row-end: recipient;
`;

GiftCardSummary.Recipient = styled.div`
  grid-column-start: content-start;
  grid-column-end: description;
  grid-row-start: recipient;
  grid-row-end: message;
  padding-bottom: 24px;
`;

GiftCardSummary.RecipientValue = styled.div`
  grid-column-start: description;
  grid-column-end: price;
  grid-row-start: recipient;
  grid-row-end: message;
  text-align: right;
  padding-bottom: 24px;
`;

GiftCardSummary.HeaderText = styled.p`
  margin: 0;
  padding-bottom: 20px;
  ${variableFontRegular({ size: '11.5px' })};
`;

GiftCardSummary.Text = styled.p`
  margin: 0;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
`;

GiftCardSummary.Message = styled.div`
  grid-column-start: content-start;
  grid-column-end: description;
  grid-row-start: message;
  grid-row-end: item-end;
  padding-bottom: 16px;
`;

GiftCardSummary.MessageValue = styled.div`
  grid-column-start: description;
  grid-column-end: price;
  grid-row-start: message;
  grid-row-end: item-end;
  text-align: right;
  padding-bottom: 16px;
`;

GiftCardSummary.PriceBreakdown = styled.div`
  display: flex;
  justify-content: space-between;
`;

GiftCardSummary.TotalText = styled.p`
  ${variableFontRegular({ size: '11.5px' })};
  margin: 0;
`;

GiftCardSummary.Links = styled.a`
  display: block;
  text-decoration: underline;
  line-height: 14px;
  margin-bottom: 4px;
`;

GiftCardSummary.HorizontalRule = styled.hr`
  margin: 16px 0 24px 0;
  opacity: 1;
`;

GiftCardSummary.Capitalize = styled.span`
  text-transform: capitalize;
`;
